import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './CriarDocumento.css';

const CriarDocumento = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiposDocumentos, setTiposDocumentos] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [anexos, setAnexos] = useState([]);

  const [formData, setFormData] = useState({
    id_tipo_documento: '',
    assunto: '',
    conteudo: '',
    data_documento: new Date().toISOString().split('T')[0],
    ano: new Date().getFullYear(),
    id_setor_destino: '',
    destino_tipo: '',
    destino_id: '',
    id_usuario_destino: '',
    prioridade: 'normal',
    observacoes: ''
  });
  const [usarCertificacao, setUsarCertificacao] = useState(false);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [tiposRes, destinosRes] = await Promise.all([
        api.get('/tipos-documentos?ativo=true'),
        api.get('/setores/destinos')
      ]);

      if (tiposRes.data.success) {
        setTiposDocumentos(tiposRes.data.data);
      }

      if (destinosRes.data.success) {
        setSetores(destinosRes.data.data);
      }
    } catch (err) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao carregar dados:', err);
      }
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar apenas PDFs
    const pdfFiles = files.filter(file => {
      if (file.type !== 'application/pdf') {
        setError(`O arquivo "${file.name}" não é um PDF. Apenas arquivos PDF são permitidos.`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`O arquivo "${file.name}" excede o tamanho máximo de 10MB.`);
        return false;
      }
      return true;
    });

    setAnexos(prev => [...prev, ...pdfFiles.map(file => ({
      file,
      id: Date.now() + Math.random(),
      nome: file.name,
      tamanho: file.size
    }))]);
  };

  const removeAnexo = (id) => {
    setAnexos(prev => prev.filter(anexo => anexo.id !== id));
  };

  const formatarTamanho = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Adicionar campos do formulário
      Object.keys(formData).forEach(key => {
        if (formData[key] && key !== 'destino_tipo' && key !== 'destino_id') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Adicionar informações do destino
      if (formData.destino_tipo && formData.destino_id) {
        formDataToSend.append('destino_tipo', formData.destino_tipo);
        formDataToSend.append('destino_id', formData.destino_id);
      }

      // Adicionar certificação digital
      if (usarCertificacao) {
        formDataToSend.append('usar_certificacao', 'true');
        // Buscar certificado do usuário
        try {
          const certResponse = await api.get('/certificados/meu-certificado');
          if (certResponse.data.success && certResponse.data.data.certificado) {
            formDataToSend.append('id_certificado', certResponse.data.data.certificado.id_certificado);
          } else {
            setError('Certificado não encontrado. Certifique-se de ter um certificado válido cadastrado.');
            setSubmitting(false);
            return;
          }
        } catch (certError) {
          setError('Erro ao buscar certificado. Certifique-se de ter um certificado válido cadastrado.');
          setSubmitting(false);
          return;
        }
      }

      // Adicionar anexos
      anexos.forEach((anexo, index) => {
        formDataToSend.append('anexos', anexo.file);
      });

      const response = await api.post('/protocolos/criar', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSuccess('Documento criado com sucesso!');
        setTimeout(() => {
          navigate('/documentos');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar documento');
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro ao criar documento:', err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados...</p>
      </div>
    );
  }

  return (
    <div className="criar-documento-container">
      <div className="page-header">
        <h1>Criar Documento</h1>
        <button className="btn-secondary" onClick={() => navigate('/documentos')}>
          Voltar
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="documento-card">
        <form onSubmit={handleSubmit} className="documento-form">
          <div className="form-section">
            <h2 className="section-title">📄 Informações do Documento</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Documento *</label>
                <select
                  value={formData.id_tipo_documento}
                  onChange={(e) => setFormData({ ...formData, id_tipo_documento: e.target.value })}
                  required
                >
                  <option value="">Selecione um tipo</option>
                  {tiposDocumentos.map(tipo => (
                    <option key={tipo.id_tipo_documento} value={tipo.id_tipo_documento}>
                      {tipo.nome} ({tipo.codigo})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Prioridade *</label>
                <select
                  value={formData.prioridade}
                  onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}
                  required
                >
                  <option value="baixa">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>

            <div className="form-group full-width">
              <label>Assunto *</label>
              <input
                type="text"
                value={formData.assunto}
                onChange={(e) => setFormData({ ...formData, assunto: e.target.value })}
                required
                placeholder="Digite o assunto do documento"
              />
            </div>

            <div className="form-group full-width">
              <label>Conteúdo</label>
              <textarea
                value={formData.conteudo}
                onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
                rows="8"
                placeholder="Digite o conteúdo do documento"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Data do Documento *</label>
                <input
                  type="date"
                  value={formData.data_documento}
                  onChange={(e) => setFormData({ ...formData, data_documento: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Ano *</label>
                <input
                  type="number"
                  value={formData.ano}
                  onChange={(e) => setFormData({ ...formData, ano: parseInt(e.target.value) })}
                  required
                  min="2000"
                  max="2100"
                />
              </div>
            </div>
          </div>

          <div className="form-divider"></div>

          <div className="form-section">
            <h2 className="section-title">📤 Destinatário</h2>
            
            <div className="form-group">
              <label>Setor Destino</label>
              <select
                value={formData.destino_id ? `${formData.destino_tipo}-${formData.destino_id}` : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    const [tipo, id] = value.split('-');
                    setFormData({ 
                      ...formData, 
                      destino_tipo: tipo,
                      destino_id: id,
                      id_setor_destino: tipo === 'setor' ? id : ''
                    });
                  } else {
                    setFormData({ 
                      ...formData, 
                      destino_tipo: '',
                      destino_id: '',
                      id_setor_destino: ''
                    });
                  }
                }}
              >
                <option value="">Selecione um destino</option>
                {setores.map((destino, index) => (
                  <option key={`${destino.tipo}-${destino.id}-${index}`} value={`${destino.tipo}-${destino.id}`}>
                    {destino.display}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Observações</label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows="4"
                placeholder="Observações adicionais sobre o documento"
              />
            </div>
          </div>

          <div className="form-divider"></div>

          <div className="form-section anexos-section">
            <h2 className="section-title">📎 Anexos (PDF)</h2>
            <p className="section-description">
              Você pode anexar até 10 arquivos PDF. Tamanho máximo por arquivo: 10MB.
            </p>

            <div className="file-upload-area">
              <input
                type="file"
                id="file-upload"
                accept=".pdf,application/pdf"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-upload" className="file-upload-label">
                <span className="upload-icon">📎</span>
                <span>Clique para selecionar arquivos PDF</span>
                <span style={{ fontSize: '0.85rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                  ou arraste e solte aqui
                </span>
              </label>
            </div>

            {anexos.length > 0 && (
              <div className="anexos-list">
                <div style={{ 
                  gridColumn: '1 / -1', 
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  color: '#6B7280',
                  fontWeight: '500'
                }}>
                  {anexos.length} arquivo{anexos.length > 1 ? 's' : ''} anexado{anexos.length > 1 ? 's' : ''}
                </div>
                {anexos.map(anexo => (
                  <div key={anexo.id} className="anexo-item">
                    <div className="anexo-info">
                      <span className="anexo-nome">{anexo.nome}</span>
                      <span className="anexo-tamanho">{formatarTamanho(anexo.tamanho)}</span>
                    </div>
                    <button
                      type="button"
                      className="btn-remove-anexo"
                      onClick={() => removeAnexo(anexo.id)}
                      aria-label="Remover anexo"
                      title="Remover anexo"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <div className="certificacao-checkbox-wrapper">
              <label className="certificacao-checkbox-label">
                <input
                  type="checkbox"
                  checked={usarCertificacao}
                  onChange={(e) => setUsarCertificacao(e.target.checked)}
                  className="certificacao-checkbox-input"
                />
                <span className="certificacao-checkbox-custom"></span>
                <span className="certificacao-checkbox-text">
                  <span className="certificacao-icon">✍️</span>
                  Assinar digitalmente com certificado
                </span>
              </label>
            </div>
            <div className="form-actions-buttons">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/documentos')}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-small"></span>
                    Criando...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    Criar Documento
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CriarDocumento;

